import { createContext, useContext, useState, useCallback } from "react";

const AdminStateContext = createContext();

export const useAdminState = () => {
  const ctx = useContext(AdminStateContext);
  if (ctx === undefined) {
    throw new Error("useAdminState must be used within an AdminStateProvider");
  }
  return ctx;
};

export const useAdminStateSafe = () => {
  return useContext(AdminStateContext) || null;
};

export const ADMIN_USER_TYPES = [
  { state: "usertype-basic", name: "UMF Basic", desc: "First time customers" },
  { state: "usertype-unlimited", name: "UMF Unlimited", desc: "Stake checking members" },
  { state: "usertype-pms", name: "PMS", desc: "Property management" },
  { state: "usertype-wallet", name: "Wallet", desc: "Digital wallet users" },
  { state: "usertype-stake-checking", name: "Stake Checking", desc: "Checking account holders" },
];

export const ADMIN_MOVEIN_STEPS = [
  { state: "mic-yes-dd", name: "Yes qualifying DD", desc: "Direct deposit active" },
  { state: "mic-no-dd", name: "No qualifying DD", desc: "Stake account not funded" },
];

export const ADMIN_CHECKING_STEPS = [
  { state: "checking-unstarted", name: "0. Unstarted", desc: "Not yet begun" },
  { state: "checking-name", name: "1. Your Full Name", desc: "First & last name as on ID" },
  { state: "checking-dob", name: "2. Date of Birth", desc: "Verify age eligibility" },
  { state: "checking-phone", name: "3. Mobile Phone", desc: "Phone number entry" },
  { state: "checking-address", name: "4. Home Address", desc: "Card shipping & verification" },
  { state: "checking-identity", name: "5. Verify Identity", desc: "SSN verification — highest drop-off" },
  { state: "checking-employment", name: "6. One More Thing", desc: "Employment details" },
  { state: "checking-agreement", name: "7. Review & Agree", desc: "Terms, disclosures, final review" },
  { state: "checking-under-review", name: "Under Review", desc: "Application pending verification" },
  { state: "checking-approved", name: "Application Approved", desc: "Awaiting debit card request" },
  { state: "checking-debit-card", name: "Debit Card Requested", desc: "Card not yet activated" },
  { state: "checking-pending-address", name: "Pending Address", desc: "Address verification in progress" },
  { state: "checking-pending-id", name: "Pending ID", desc: "Identity verification in progress" },
  { state: "checking-pending-ssn", name: "Pending SSN", desc: "SSN verification in progress" },
  { state: "checking-denied", name: "Application Denied", desc: "Terminal state — cannot proceed" },
  { state: "checking-ineligible", name: "Ineligible", desc: "Does not meet criteria" },
  { state: "checking-done", name: "✓ Finished", desc: "Card removed from slider" },
];

export const ADMIN_DD_STEPS = [
  { state: "dd-unstarted", name: "0. Unstarted", desc: "Not yet qualifying" },
  { state: "dd-add-funds", name: "1. Add Funds", desc: "Choose funding method" },
  { state: "dd-employer", name: "2. Connect Employer", desc: "Search & link payroll" },
  { state: "dd-confirm", name: "3. Confirm Setup", desc: "Review deposit details" },
  { state: "dd-done", name: "✓ Finished", desc: "Card removed from slider" },
  { state: "dd-locked", name: "Locked", desc: "Prerequisite not met" },
];

export const ADMIN_CREDIT_BUILDER = [
  { state: "cb-eligible-t", name: "Eligible: True", desc: "eligible_for_credit_reporting__c = True", apiField: "eligible_for_credit_reporting__c", apiValue: "True" },
  { state: "cb-eligible-f", name: "Eligible: False", desc: "eligible_for_credit_reporting__c = False", apiField: "eligible_for_credit_reporting__c", apiValue: "False" },
  { state: "cb-optedin-t", name: "Opted In: True", desc: "opted_in_to_credit_reporting__c = True", apiField: "opted_in_to_credit_reporting__c", apiValue: "True" },
  { state: "cb-optedin-f", name: "Opted In: False", desc: "opted_in_to_credit_reporting__c = False", apiField: "opted_in_to_credit_reporting__c", apiValue: "False" },
];

export const ADMIN_CASHBACK_RENT = [
  { state: "rent-not-available", name: "Not Available", desc: "Cash back on rent not available" },
  { state: "rent-not-earning", name: "Not Earning", desc: "Cash back not activated" },
  { state: "rent-earning", name: "Earning", desc: "$18.50/mo active" },
  { state: "rent-locked-checking", name: "Locked (Checking)", desc: "Stake Checking not set up" },
  { state: "rent-locked-funds", name: "Locked (Add Funds)", desc: "Must add funds first" },
];

export const ADMIN_SPEND_TRACKER = [
  { state: "spend-0", name: "0% — $0 spent", desc: "Month start", pct: 0, spent: "$0", progressDesc: "$250 more to unlock", ctaGray: "Spend $250 this month to earn", ctaGreen: "$10 Cash Back on Rent" },
  { state: "spend-50", name: "50% — $125 spent", desc: "Halfway there", pct: 50, spent: "$125", progressDesc: "$125 more to unlock", ctaGray: "Halfway there — $125 more to go", ctaGreen: "unlocks your $10 Cash Back on Rent" },
  { state: "spend-75", name: "75% — $188 spent", desc: "Almost there", pct: 75, spent: "$188", progressDesc: "$62 more to unlock", ctaGray: "Almost there — $62 more to go", ctaGreen: "unlocks your $10 Cash Back on Rent" },
  { state: "spend-90", name: "90% — $225 spent", desc: "So close", pct: 90, spent: "$225", progressDesc: "$25 more to unlock", ctaGray: "So close! Only $25 left", ctaGreen: "use your Stake card for any purchase" },
  { state: "spend-100", name: "100% — $250+ spent", desc: "Qualified — reward unlocked", pct: 100, spent: "$250+", progressDesc: "Goal complete", ctaGray: "", ctaGreen: "" },
];

export const ADMIN_CONNECTED_ACCOUNTS = [
  { state: "connected-zero", name: "Zero Accounts", desc: "No linked accounts" },
  { state: "connected-one", name: "One Account", desc: "External account linked" },
];

export const ADMIN_RECURRING_DEPOSITS = [
  { state: "recurring-none", name: "No Recurring Deposits", desc: "No scheduled deposits" },
  { state: "recurring-yes", name: "Yes Recurring Deposits", desc: "Active recurring schedule" },
];

export const ADMIN_STAKE_DEBIT = [
  { state: "sd-approved", name: "Application Approved", desc: "status = ApplicationApproved", apiField: "status", apiValue: "ApplicationApproved" },
  { state: "sd-deposit-created", name: "Deposit Account Created", desc: "status = DepositAccountCreated", apiField: "status", apiValue: "DepositAccountCreated" },
  { state: "sd-card-requested", name: "Debit Card Requested", desc: "status = DebitCardRequested", apiField: "status", apiValue: "DebitCardRequested" },
  { state: "sd-card-activated", name: "Debit Card Activated", desc: "status = DebitCardActivated", apiField: "status", apiValue: "DebitCardActivated" },
];

export const ADMIN_INSTANT_CASH_OUT = [
  { state: "ico-enabled-t", name: "Enabled: True", desc: "enabled_for_instant_transfer__c = True", apiField: "enabled_for_instant_transfer__c", apiValue: "True" },
  { state: "ico-enabled-f", name: "Enabled: False", desc: "enabled_for_instant_transfer__c = False", apiField: "enabled_for_instant_transfer__c", apiValue: "False" },
];

export const ADMIN_STAKE_SAVER = [
  { state: "ss-registered", name: "Registered", desc: "resident_registration_status__c = registered", apiField: "resident_registration_status__c", apiValue: "registered" },
  { state: "ss-unregistered", name: "Unregistered", desc: "resident_registration_status__c = null", apiField: "resident_registration_status__c", apiValue: "" },
];

export const AdminStateProvider = ({ children }) => {
  const [mode, setMode] = useState("live");
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelHeight, setPanelHeight] = useState("25vh");

  const [selUserType, setSelUserType] = useState("usertype-unlimited");
  const [selMovein, setSelMovein] = useState("mic-no-dd");
  const [selChecking, setSelChecking] = useState("checking-unstarted");
  const [selDirectDeposit, setSelDirectDeposit] = useState("dd-unstarted");
  const [selCreditBuilder, setSelCreditBuilder] = useState(null);
  const [selCashback, setSelCashback] = useState("rent-not-available");
  const [selSpend, setSelSpend] = useState("spend-0");
  const [selConnectedAccounts, setSelConnectedAccounts] = useState("connected-zero");
  const [selRecurringDeposits, setSelRecurringDeposits] = useState("recurring-none");
  const [selStakeDebit, setSelStakeDebit] = useState(null);
  const [selInstantCashOut, setSelInstantCashOut] = useState(null);
  const [selStakeSaver, setSelStakeSaver] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  const isMockMode = mode === "mock";

  const toggleMode = useCallback(() => {
    setMode((prev) => (prev === "live" ? "mock" : "live"));
  }, []);

  const togglePanel = useCallback(() => {
    setPanelOpen((prev) => !prev);
  }, []);

  return (
    <AdminStateContext.Provider
      value={{
        mode,
        setMode,
        toggleMode,
        isMockMode,
        panelOpen,
        setPanelOpen,
        togglePanel,
        panelHeight,
        setPanelHeight,
        selUserType,
        setSelUserType,
        selMovein,
        setSelMovein,
        selChecking,
        setSelChecking,
        selDirectDeposit,
        setSelDirectDeposit,
        selCreditBuilder,
        setSelCreditBuilder,
        selCashback,
        setSelCashback,
        selSpend,
        setSelSpend,
        selConnectedAccounts,
        setSelConnectedAccounts,
        selRecurringDeposits,
        setSelRecurringDeposits,
        selStakeDebit,
        setSelStakeDebit,
        selInstantCashOut,
        setSelInstantCashOut,
        selStakeSaver,
        setSelStakeSaver,
        zoomLevel,
        setZoomLevel,
      }}
    >
      {children}
    </AdminStateContext.Provider>
  );
};
